/**
 * 接口列表（method → url）
 *
 * 命名约定：
 *  - jmall.* ：通过 jkymini 网关打到 jmall 服务（统一 jmall C 端命名空间）
 *  - jmall.login.* ：C 端登录相关接口，打到架构登录服务（不是 jkymini）
 *  - jmall.transport.* ：转发到其他业务组（jkymini 网关内处理）
 *
 * 本文件合并自：
 *  1) 小程序 JMemberCenter_WeChatApp/utils/request-utils/request-methoh.js
 *  2) PC 端实际使用且后台有定义的方法（JackYunApiController.cs 中的 case）
 *  3) PC 端专属方法（PC 前端独有的拼写、命名）
 *
 * 注意事项：
 *  - PC 端有几条和小程序命名不一致的方法，本文件保留两套 method key，
 *    调用方继续使用 PC 自有拼写，url 仍按小程序规范。
 *  - PC 中存在的拼写错误 method（如 jmall.activiy.*）作为兼容保留。
 */

const baseMethodList = {
  // 吉商城访问其他业务组接口统一转发接口
  "jmall.transport.callfeign": {
    url: "/jmall/transport/callfeign",
    type: "POST"
  },
  // 吉商城访问其他业务组接口统一转发接口V1
  "jmall.transport.callfeign.v1": {
    url: "/jmall/transport/callfeign/v1",
    type: "POST"
  },
  // 吉商城参数获取接口
  "jmall.param.getparamlist": {
    url: "/jmall/param/getparam",
    type: "POST"
  },
  // 吉商城参数获取接口 V1
  "jmall.param.getparamlistv1": {
    url: "/jmall/param/getparam/v1",
    type: "POST"
  },
  // PC 端使用的方法（按后台定义）
  "jmall.param.getparamv1": {
    url: "/jmall/param/getparam/v1",
    type: "POST"
  },
  // 老版本参数获取
  "jmall.param.getparam": {
    url: "/jmall/param/getparam",
    type: "POST"
  },
  // 配置参数（PC 后台 common.cfg.getparam 兼容保留）
  "common.cfg.getparam": {
    url: "/jmall/param/getparam",
    type: "POST"
  }
};

/**
 * 会员相关接口
 */
const customerMethodList = {
  // 人机验证码
  "jmall.captcha.generate": {
    url: "/jmall/captcha/generate",
    type: "POST"
  },
  "jmall.captcha.check": {
    url: "/jmall/captcha/check",
    type: "POST"
  },
  "jmall.customer.queryshareposter": {
    url: "/jmall/customer/queryshareposter",
    type: "Get"
  },
  "jmall.customer.addshareposter": {
    url: "/jmall/customer/addshareposter",
    type: "Get"
  },
  "jmall.customer.balance": {
    url: "/jmall/customer/getbalance",
    type: "POST",
    bAuth: true
  },
  "jmall.sms.sendverificationcode": {
    url: "/jmall/customer/send/verificationcode",
    type: "POST"
  },
  "jmall.sms.checkverificationcode": {
    url: "/jmall/customer/check/verificationcode",
    type: "POST"
  },
  "jmall.customer.getcompanycollectstatus": {
    url: "/jmall/customer/getcompanycollectstatus",
    type: "POST"
  },
  "jmall.customer.updatecustomerinfo": {
    url: "/jmall/customer/updateinfo",
    type: "POST",
    bAuth: true
  },
  "jmall.customer.membershiplist": {
    url: "/jmall/premiummember/listpremiummembership",
    type: "GET"
  },
  // 登录接口-旧（PC 后台有 switch case）
  "jmall.customer.login": {
    url: "/jmall/customer/login",
    type: "POST"
  },
  // 登录接口-新
  "jmall.customer.getdetail": {
    url: "/jmall/customer/getdetail",
    type: "POST"
  },
  "jmall.customer.register": {
    url: "/jmall/customer/registernewcustomer",
    type: "POST"
  },
  "jmall.customer.registerforwechat": {
    url: "/jmall/customer/registerforwechat",
    type: "POST"
  },
  "jmall.customer.updatenameandpwd": {
    url: "/jmall/customer/updatenameandpwd",
    type: "POST",
    bAuth: true
  },
  "jmall.customer.gettelbyid": {
    url: "/jmall/customer/gettelbyid",
    type: "POST"
  },
  "jmall.customer.setcompanycollectstatus": {
    url: "/jmall/customer/setcompanycollectstatus",
    type: "POST"
  },
  "jmall.customer.getdecrypttel": {
    url: "/jmall/customer/decrypt/tel",
    type: "POST"
  },
  "jmall.customer.getcustomerapplydetail": {
    url: "/jmall/customer/get/apply/detail",
    type: "POST"
  },
  "jmall.customer.addcustomerapply": {
    url: "/jmall/customer/add/apply",
    type: "POST"
  },
  "jmall.customer.addsourcechannel": {
    url: "/jmall/customer/addsourceinfo",
    type: "POST"
  },
  // PC 端使用
  "jmall.customer.auditcustomerapply": {
    url: "/jmall/customer/auditcustomerapply",
    type: "POST"
  },
  "jmall.customer.getwxlogininfo": {
    url: "/jmall/weixin/getacctokenbycode",
    type: "POST"
  },
  "jmall.customer.updatecuspcopenid": {
    url: "/jmall/customer/updatecuspcopenid",
    type: "POST"
  },
  // 架构内部 - customer 实体（旧接口，到 admin 处理）
  "customer.realbalance": {
    url: "/customer/realbalance",
    type: "POST"
  },
  "customer.realbalancenew": {
    url: "/customer/realbalancenew",
    type: "POST"
  }
};

/**
 * 订单相关接口
 */
const tradeMethodList = {
  "jmall.trade.postagecalc": {
    url: "/jmall/trade/postagecalc/v1",
    type: "POST"
  },
  "jmall.trade.checkisfreepostage": {
    url: "/jmall/trade/checkisfreepostage",
    type: "POST"
  },
  "jmall.trade.tradecreateforpremiummembership": {
    url: "/jmall/trade/tradecreateforpremiummembership",
    type: "POST",
    bAuth: true
  },
  "jmall.trade.tradecreate": {
    url: "/jmall/trade/tradecreate",
    type: "POST",
    bAuth: true
  },
  "jmall.trade.tradecancel": {
    url: "/jmall/trade/cancel",
    type: "POST",
    bAuth: true
  },
  "jmall.trade.tradecount": {
    url: "/jmall/trade/tradelist/count",
    type: "POST",
    bAuth: true
  },
  "jmall.trade.tradelist": {
    url: "/jmall/trade/tradelist",
    type: "POST",
    bAuth: true
  },
  "jmall.trade.tradedetail": {
    url: "/jmall/trade/tradedetail",
    type: "POST",
    bAuth: true
  },
  "jmall.trade.gettradepayinfo": {
    url: "/jmall/trade/gettradepayinfo",
    type: "POST",
    bAuth: true
  },
  "jmall.trade.payorderfinalpayment": {
    url: "/jmall/trade/payorderfinalpayment",
    type: "POST"
  },
  "jmall.trade.pushtradetooms": {
    url: "/jmall/trade/pushtradetooms",
    type: "POST"
  },
  "jmall.trade.getcontinuepaytradeno": {
    url: "/jmall/trade/getcontinuepaytradeno",
    type: "POST"
  },
  "jmall.trade.updatetradebaseinfo": {
    url: "/jmall/trade/updatetradebaseinfo",
    type: "POST"
  },
  "jmall.trade.calfreepostagedifference": {
    url: "/jmall/trade/calfreepostagedifference",
    type: "POST"
  },
  "jmall.trade.createunpaidtradereminder": {
    url: "/jmall/reminder/createunpaidtradereminder",
    type: "POST"
  },
  // PC 端使用，与小程序同名但 url 不同
  "jmall.trade.tradelist.totalamount": {
    url: "/jmall/trade/tradelist/totalamount",
    type: "POST"
  },
  "jmall.trade.insertplanwaitpay": {
    url: "/jmall/trade/insertplanwaitpay",
    type: "POST"
  },
  "jmall.trade.gettradestatus": {
    url: "/jmall/trade/gettradestatus",
    type: "POST"
  },
  "jmall.trade.gettradestatusv1": {
    url: "/jmall/trade/gettradestatusv1",
    type: "POST"
  },
  // PC 端使用：限购检测。后端 switch case 名是 jmall.trade.checkrestrictedpurchase（JackYunApiController:1731），
  // 实际转发 URL 是 jmall/restricted/checkrestrictedpurchase（JmallTradeBLL.checkRestrictedPurchase）
  "jmall.trade.checkrestrictedpurchase": {
    url: "/jmall/restricted/checkrestrictedpurchase",
    type: "POST"
  },
  // PC 端使用 - 订单保留字段信息
  "jmall.trade.updatetradereservedinfo": {
    url: "/jmall/trade/updatereservedinfo",
    type: "POST"
  },
  "jmall.trade.getreservedinfo": {
    url: "/jmall/trade/getreservedinfo",
    type: "POST"
  },
  "jmall.trade.getstaffbuyinfo": {
    url: "/jmall/trade/getstaffbuyinfo",
    type: "POST"
  },
  "jmall.trade.gettradegoodsmarketinginfo": {
    url: "/jmall/trade/gettradegoodsmarketinginfo",
    type: "POST"
  },
  "jmall.trade.addtradegoodsstockhold": {
    url: "/jmall/trade/addtradegoodsstockhold",
    type: "POST"
  },
  "jmall.trade.releasetradegoodsstockhold": {
    url: "/jmall/trade/releasetradegoodsstockhold",
    type: "POST"
  },
  "jmall.trade.addtradepayinfo": {
    url: "/jmall/trade/addtradepayinfo",
    type: "POST"
  },
  "jmall.trade.getrefundstatusbyjackyuntradeno": {
    url: "/jmall/refund/sellback/status",
    type: "POST"
  },
  "jmall.trade.createsellbackorder": {
    url: "/jmall/refund/createsellbackorder",
    type: "POST"
  },
  "jmall.trade.getrefundedgoodslistbyjackyuntradeno": {
    url: "/jmall/refund/sellback/goodsinfobyjkytradeno",
    type: "POST"
  },
  "jmall.trade.addtradelog": {
    url: "/jmall/trade/addtradeloginfo",
    type: "POST"
  },
  // PC 端使用：与小程序 changescore 不同
  "jmall.trade.changetradescore": {
    url: "/jmall/trade/change/score",
    type: "POST",
    bAuth: true
  },
  "jmall.trade.changescore": {
    url: "/jmall/trade/change/score",
    type: "POST",
    bAuth: true
  },
  "jmall.trade.closetradepay": {
    url: "/jmall/trade/closepayinfo",
    type: "POST"
  },
  // PC 端使用
  "jmall.trade.canceltrade": {
    url: "/jmall/trade/cancel",
    type: "POST"
  },
  "jmall.trade.getpicbytradeidlist": {
    url: "/jmall/trade/batch/query/pic",
    type: "POST"
  },
  "jmall.trade.getcouponusedetail": {
    url: "/jmall/trade/batch/query/coupon/use/detail",
    type: "POST"
  },
  "jmall.trade.gettradewxinfo": {
    url: "/jmall/trade/getwxinfo",
    type: "POST"
  },
  "jmall.trade.getwxorderinfo": {
    url: "/jmall-single/wx/plat/getorder",
    type: "POST"
  },
  "jmall.trade.syncwxorderdeliveryinfo": {
    url: "/jmall/trade/syncwxorderdeliveryinfo",
    type: "POST"
  },
  "jmall.trade.getssysettleinfo": {
    url: "/jmall/pay/ssy/settle",
    type: "POST"
  },

  // 老 trade.* 接口（PC 后台 switch case 内部处理，由 admin 后台自己来）
  "trade.tradelist": { url: "/trade/tradelist", type: "POST" },
  "trade.tradecount": { url: "/trade/tradecount", type: "POST" },
  "trade.tradepaylist": { url: "/trade/tradepaylist", type: "POST" },
  "trade.tradedetail": { url: "/trade/tradedetail", type: "POST" },
  "trade.decryptdata": { url: "/trade/decryptdata", type: "POST" },
  "trade.tradedetail2": { url: "/trade/tradedetail2", type: "POST" },
  "trade.payment": { url: "/trade/payment", type: "POST" },
  "trade.tradelog": { url: "/trade/tradelog", type: "POST" },
  "trade.logisticinfo": { url: "/trade/logisticinfo", type: "POST" },
  "trade.logisticvideo": { url: "/trade/logisticvideo", type: "POST" },
  "trade.addtrade": { url: "/trade/addtrade", type: "POST" },
  "trade.deliverlog": { url: "/trade/deliverlog", type: "POST" },
  "trade.getpostage": { url: "/trade/getpostage", type: "POST" },
  "trade.calcpostage": { url: "/trade/calcpostage", type: "POST" },
  "trade.arrive": { url: "/trade/arrive", type: "POST" },
  "trade.pickupcode": { url: "/trade/pickupcode", type: "POST" },
  "trade.creatsellback": { url: "/trade/creatsellback", type: "POST" },
  "trade.creatsellback_new": { url: "/trade/creatsellback_new", type: "POST" },
  "trade.cancel": { url: "/trade/cancel", type: "POST" },
  "trade.delete": { url: "/trade/delete", type: "POST" },
  "trade.cancelsellback": { url: "/trade/cancelsellback", type: "POST" },
  "trade.cancelsellback_new": { url: "/trade/cancelsellback_new", type: "POST" },
  "trade.sellbacklist": { url: "/trade/sellbacklist", type: "POST" },
  "trade.sellbacklist_new": { url: "/trade/sellbacklist_new", type: "POST" },
  "trade.sellbackbacktoaudit": { url: "/trade/sellbackbacktoaudit", type: "POST" },
  "trade.sellbackbacktoaudit_new": { url: "/trade/sellbackbacktoaudit_new", type: "POST" },
  "trade.freeze": { url: "/trade/freeze", type: "POST" },
  "trade.thaw": { url: "/trade/thaw", type: "POST" },
  "trade.chain": { url: "/trade/chain", type: "POST" },
  "trade.edit": { url: "/trade/edit", type: "POST" },
  "trade.adranalyze": { url: "/trade/adranalyze", type: "POST" },
  "trade.createtradeno": { url: "/trade/createtradeno", type: "POST" }
};

/**
 * 货品相关接口
 */
const goodsMethodList = {
  // PC 后台有定义，老的 goods.* 走 admin
  "goods.goodslist": { url: "/goods/goodslist", type: "POST" },
  "goods.goodsstocks": { url: "/goods/goodsstocks", type: "POST" },
  "goods.goodsexchange": { url: "/goods/goodsexchange", type: "POST" },
  "goods.catelist": { url: "/goods/catelist", type: "POST" },
  "goods.allgoodslist": { url: "/goods/allgoodslist", type: "POST" },
  "goods.allgoodsinfoandbaseunit": { url: "/goods/allgoodsinfoandbaseunit", type: "POST" },
  "goods.countgoodsinfo": { url: "/goods/countgoodsinfo", type: "POST" },
  "goods.getgoodsinfoandbusinessattr": { url: "/goods/getgoodsinfoandbusinessattr", type: "POST" },
  "goods.listbyids": { url: "/goods/listbyids", type: "POST" },
  "goods.skulistbygoodsid": { url: "/goods/skulistbygoodsid", type: "POST" },
  "goods.skuinfo": { url: "/goods/skuinfo", type: "POST" },
  "goods.skuprice": { url: "/goods/skuprice", type: "POST" },
  "goods.memberprice": { url: "/goods/memberprice", type: "POST" },
  "goods.channelmemberprice": { url: "/goods/channelmemberprice", type: "POST" },
  "goods.getgoodsinfo": { url: "/goods/getgoodsinfo", type: "POST" },
  "goods.getskucustominfo": { url: "/goods/getskucustominfo", type: "POST" },
  "goods.getpackagegoodsstock": { url: "/goods/getpackagegoodsstock", type: "POST" },
  "goods.goodsdescript": { url: "/goods/goodsdescript", type: "POST" },
  "goods.skulist": { url: "/goods/skulist", type: "POST" },
  "goods.listsku": { url: "/goods/listsku", type: "POST" },
  "goods.countsku": { url: "/goods/countsku", type: "POST" },
  "goods.skuimglist": { url: "/goods/skuimglist", type: "POST" },
  "goods.customfield": { url: "/goods/customfield", type: "POST" },
  "goods.searchfield": { url: "/goods/searchfield", type: "POST" },
  "goods.getgoodsflag": { url: "/goods/getgoodsflag", type: "POST" },
  "goods.goodsstockserial": { url: "/goods/goodsstockserial", type: "POST" },
  "goods.getgoodsbybarcode": { url: "/goods/getgoodsbybarcode", type: "POST" },
  "goods.getgoodsbyserialno": { url: "/goods/getgoodsbyserialno", type: "POST" },
  "goods.getpurchaseinfo": { url: "/goods/getpurchaseinfo", type: "POST" },
  "goods.getbrandlist": { url: "/goods/getbrandlist", type: "POST" },
  "goods.getbrandlistopen": { url: "/goods/getbrandlistopen", type: "POST" },
  "goods.getbathlistbyskuid": { url: "/goods/getbathlistbyskuid", type: "POST" },
  "goods.getbygoodsid": { url: "/goods/getbygoodsid", type: "POST" },
  "goods.goodslistforjmall": { url: "/goods/goodslistforjmall", type: "POST" },
  "goods.goodslistcountforjmall": { url: "/goods/goodslistcountforjmall", type: "POST" },
  "goods.updatesynchflag": { url: "/goods/updatesynchflag", type: "POST" },
  "goods.getskulistbyid": { url: "/goods/getskulistbyid", type: "POST" },
  "goods.listsalespricetype": { url: "/goods/listsalespricetype", type: "POST" },

  // jmall.* 接口（PC 后台转发或 jmall 服务直接处理）
  "jmall.goods.listsimplespecinfo": {
    url: "/jmall/goods/listsimplespecinfo",
    type: "POST"
  },
  "jmall.goods.listgoods": {
    url: "/jmall/goods/listgoods",
    type: "POST"
  },
  "jmall.goods.getgoodslist": {
    url: "/jmall/goods/listgoodsnew",
    type: "POST"
  },
  "jmall.goods.getgoodsextrainfo": {
    url: "/jmall/goods/getgoodsextrainfo",
    type: "POST"
  },
  "jmall.goods.getgoodscategorylist": {
    url: "/jmall/goods/listmallcategory",
    type: "POST"
  },
  "jmall.goods.getgoodscategorylistv2": {
    url: "/jmall/goods/listmallcategory/v2",
    type: "POST"
  },
  "jmall.goods.getgoodscategorylistv3": {
    url: "/jmall/goods/listmallcategory/v3",
    type: "POST"
  },
  "jmall.goods.getgoodsrecommendinfo": {
    url: "/jmall/goods/getgoodsrecommendinfo",
    type: "POST"
  },
  "jmall.goods.getbrandlist": {
    url: "/jmall/goods/brand/list",
    type: "POST"
  },
  "jmall.goods.checkgoodsstock": {
    url: "/jmall/goods/checkgoodsstock",
    type: "POST"
  },
  "jmall.goods.checkgoodserpstock": {
    url: "/jmall/goods/check/base/stock",
    type: "POST"
  },
  "jmall.goods.checkgoodsactivitystock": {
    url: "/jmall/activity/goods/buycount/query",
    type: "POST"
  },
  "jmall.goods.checkgoodsboughtlimit": {
    url: "/jmall/goods/check/customer/limit",
    type: "POST"
  },
  "jmall.goods.listmingoodsprice": {
    url: "/jmall/goods/listmingoodsprice",
    type: "POST"
  },
  "jmall.goods.listmingoodspriceforpackage": {
    url: "/jmall/goods/listmingoodsprice/package",
    type: "POST"
  },
  "jmall.goods.getunpaidtradegoodssellcount": {
    url: "/jmall/goods/getunpaidtradegoodssellcount",
    type: "POST"
  },
  "jmall.goods.getshoppingcartlist": {
    url: "/jmall/goods/getshopcartlist",
    type: "POST"
  },
  "jmall.goods.getshoppingcartcount": {
    url: "/jmall/goods/countshopcartgoods",
    type: "POST"
  },
  // PC 端使用：与小程序的 getshoppingcartcount 不同
  "jmall.goods.getshopcartcountgroupbygoods": {
    url: "/jmall/goods/batchget/shopcart/goodscount",
    type: "POST"
  },
  "jmall.goods.updateshopcartgoodscount": {
    url: "/jmall/goods/updateshopcartgoodscount",
    type: "POST"
  },
  "jmall.goods.deleteshoppingcart": {
    url: "/jmall/goods/deleteshopcartgoods",
    type: "POST"
  },
  "jmall.goods.addshopcart": {
    url: "/jmall/goods/addshopcart",
    type: "POST"
  },
  "jmall.goods.batchaddshopcart": {
    url: "/jmall/goods/batchaddshopcart",
    type: "POST"
  },
  "jmall.goods.getgoodsdetail": {
    url: "/jmall/goods/getgoodsdetail",
    type: "POST"
  },
  "jmall.goods.getskulist": {
    url: "/jmall/goods/getskulist",
    type: "POST"
  },
  "jmall.goods.getgoodsrichtext": {
    url: "/jmall/goods/getgoodsrichtext",
    type: "POST"
  },
  "jmall.goods.getservicelist": {
    url: "/jmall/goods/service/list",
    type: "POST"
  },
  "jmall.goods.getboundlabellist": {
    url: "/jmall/goods/getboundlabellist",
    type: "POST"
  },
  "jmall.goods.listgoodsprice": {
    url: "/jmall/goods/listgoodsprice",
    type: "POST"
  },
  "jmall.goods.getskupricelist": {
    url: "/jmall/goods/listskuprice",
    type: "POST"
  },
  "jmall.goods.getgoodsstocknew": {
    url: "/jmall/goods/getgoodsstocknew",
    type: "POST"
  },
  "jmall.goods.getgoodsstocklist": {
    url: "/jmall/goods/getgoodsstocklist",
    type: "POST"
  },
  "jmall.goods.getgoodssumstocklist": {
    url: "/jmall/goods/getgoodssumstocklist",
    type: "POST"
  },
  "jmall.goods.getskustocklist": {
    url: "/jmall/goods/getskustocklist",
    type: "POST"
  },
  // PC 端使用：小程序是 listvaliditydatebymuticondition
  "jmall.goods.listvaliditydate": {
    url: "/jmall/goods/listvaliditydate",
    type: "POST"
  },
  "jmall.goods.listvaliditydatebymuticondition": {
    url: "/jmall/goods/listvaliditydatebymuticondition",
    type: "POST"
  },
  "jmall.goods.getjackyungoodsextendedinfo": {
    url: "/jmall/goods/getjackyungoodsextendedinfo",
    type: "POST"
  },
  "jmall.goods.checkisonsales": {
    url: "/jmall/goods/checkisonsales",
    type: "POST"
  },
  "jmall.goods.policy.getmemberlevelblockgoodslist": {
    url: "/jmall/goods/policy/getmemberlevelblockgoodslist",
    type: "POST"
  },
  "jmall.goods.addcollection": {
    url: "/jmall/goods/addgoodscollection",
    type: "POST"
  },
  "jmall.goods.deletecollection": {
    url: "/jmall/goods/deletegoodscollection",
    type: "POST"
  },
  "jmall.goods.getcollectionid": {
    url: "/jmall/goods/iscollectiongoods",
    type: "POST"
  },
  // PC 端使用：小程序命名没这个
  "jmall.goods.iscollectiongoods": {
    url: "/jmall/goods/iscollectiongoods",
    type: "POST"
  },
  "jmall.goods.getcollectiongoodslist": {
    url: "/jmall/goods/getcollectiongoodslist",
    type: "POST"
  },
  // PC 端使用
  "jmall.goods.countcollectiongoodslist": {
    url: "/jmall/goods/countcollectiongoodslist",
    type: "POST"
  },
  // PC 端使用：注意 'List' 大写
  "jmall.goods.deletecollectionList": {
    url: "/jmall/goods/deletecollectionList",
    type: "POST"
  },
  "jmall.goods.getgoodscommentlist": {
    url: "/jmall/goods/comment/list",
    type: "POST"
  },
  "jmall.goods.getgoodscommentcount": {
    url: "/jmall/goods/comment/count",
    type: "POST"
  },
  "jmall.goods.addgoodscomment": {
    url: "/jmall/goods/comment/add",
    type: "POST"
  },
  "jmall.goods.getgoodsgrouplist": {
    url: "/jmall/goods/getgoodsgrouplist",
    type: "POST"
  },
  "jmall.goods.getgoodsexpectedcommission": {
    url: "/jmall/goods/getgoodsexpectedcommission",
    type: "POST"
  },
  "jmall.goods.getgoodsidbygoodsnoorspeccode": {
    url: "/jmall/goods/getgoodsidbygoodsnoorspeccode",
    type: "POST"
  },
  "jmall.goods.gettradecommentstatus": {
    url: "/jmall/goods/comment/tradecommentstatus",
    type: "POST"
  },
  "jmall.goods.getlistgoodsnewcount": {
    url: "/jmall/goods/listgoodsnew/count",
    type: "POST"
  },
  "jmall.goods.getsimplegoodslist": {
    url: "/jmall/goods/listsimplegoods",
    type: "POST"
  },
  "jmall.goods.getgovsubsidizedgoodsinfo": {
    url: "/jmall/activity/govsubsidizedgoods/getgovsubsidizedgoodsinfo",
    type: "POST"
  },
  "jmall.goods.checkandgetgovsubsidizedgoodsidlist": {
    url: "/jmall/activity/govsubsidizedgoodsid/list",
    type: "POST"
  },
  "jmall.goods.getspecexpectedcommission": {
    url: "/jmall/goods/getspecexpectedcommission",
    type: "POST"
  },
  "jmall.goods.getbygoodsid": {
    url: "/jmall/goods/getbygoodsid",
    type: "POST"
  },
  "jmall.goods.getcustomfield": {
    url: "/jmall/goods/getcustomfield",
    type: "POST"
  },
  // PC 端使用
  "jmall.goods.getgoodslimitdatalist": {
    url: "/jmall/goods/get/customer/bought",
    type: "POST"
  },
  "jmall.goods.getfetchflags": {
    url: "/jmall/goods/getfetchflags",
    type: "POST"
  },
  // PC 端使用：拼写有差异（goodsbrandlist 不是 getgoodsbrandlist）
  "jmall.goods.goodsbrandlist": {
    url: "/jmall/goods/brand/list",
    type: "POST"
  },
  // 后台 jmall.goods.* 全部存在
  "jmall.goods.syncgoods": { url: "/jmall/goods/syncgoods", type: "POST" },
  "jmall.goods.getexistgoodsids": { url: "/jmall/goods/getexistgoodsids", type: "POST" },
  "jmall.goods.listsimplegoods": { url: "/jmall/goods/listsimplegoods", type: "POST" },
  "jmall.goods.getgoodscount": { url: "/jmall/goods/getgoodscount", type: "POST" },
  "jmall.goods.gettimedgoods": { url: "/jmall/goods/gettimedgoods", type: "POST" },
  "jmall.goods.saveerpgoodscategory": { url: "/jmall/goods/saveerpgoodscategory", type: "POST" },
  "jmall.goods.matcherpgoodscategory": { url: "/jmall/goods/matcherpgoodscategory", type: "POST" },
  "jmall.goods.batchspecchangeshelf": { url: "/jmall/goods/batchspecchangeshelf", type: "POST" },
  "jmall.goods.upshelvesspecinshelfgoods": { url: "/jmall/goods/upshelvesspecinshelfgoods", type: "POST" },
  "jmall.goods.getmanagegoodslist": { url: "/jmall/goods/getmanagegoodslist", type: "POST" },
  "jmall.goods.getmanagestylegoodslist": { url: "/jmall/goods/getmanagestylegoodslist", type: "POST" },
  "jmall.goods.getmanagegoodscount": { url: "/jmall/goods/getmanagegoodscount", type: "POST" },
  "jmall.goods.policy.getlimitedsalespolicylist": { url: "/jmall/goods/policy/getlimitedsalespolicylist", type: "POST" },
  "jmall.goods.policy.savelimitedsalespolicy": { url: "/jmall/goods/policy/savelimitedsalespolicy", type: "POST" },
  "jmall.goods.policy.deltelimitedsalespolicy": { url: "/jmall/goods/policy/deltelimitedsalespolicy", type: "POST" },
  "jmall.goods.policy.getlimitedsalespolicygoodslist": { url: "/jmall/goods/policy/getlimitedsalespolicygoodslist", type: "POST" },
  "jmall.goods.policy.savememberlevelblockgoods": { url: "/jmall/goods/policy/savememberlevelblockgoods", type: "POST" },
  "jmall.goods.policy.deltememberlevelblockgoods": { url: "/jmall/goods/policy/deltememberlevelblockgoods", type: "POST" },
  "jmall.goods.policy.getgoodsstockpolicylist": { url: "/jmall/goods/policy/getgoodsstockpolicylist", type: "POST" },
  "jmall.goods.policy.getgoodsstockpolicydetaillist": { url: "/jmall/goods/policy/getgoodsstockpolicydetaillist", type: "POST" },
  "jmall.goods.policy.savegoodsstockpolicy": { url: "/jmall/goods/policy/savegoodsstockpolicy", type: "POST" },
  "jmall.goods.policy.deltegoodsstockpolicy": { url: "/jmall/goods/policy/deltegoodsstockpolicy", type: "POST" },
  "jmall.goods.importrecommendgoods": { url: "/jmall/goods/importrecommendgoods", type: "POST" },
  "jmall.goods.importpromotiongoods": { url: "/jmall/goods/importpromotiongoods", type: "POST" },
  "jmall.goods.importtimesalegoods": { url: "/jmall/goods/importtimesalegoods", type: "POST" },
  "jmall.goods.batchrefreshprice": { url: "/jmall/goods/batchrefreshprice", type: "POST" },
  "jmall.goods.getskuunitinfo": { url: "/jmall/goods/getskuunitinfo", type: "POST" },
  "jmall.goods.batchupdategoods": { url: "/jmall/goods/batchupdategoods", type: "POST" },
  "jmall.goods.updatevirtualsalescount": { url: "/jmall/goods/updatevirtualsalescount", type: "POST" },
  "jmall.goods.getgoodschannelinfo": { url: "/jmall/goods/getgoodschannelinfo", type: "POST" },
  "jmall.goods.setchannelgoodsforgoods": { url: "/jmall/goods/setchannelgoodsforgoods", type: "POST" },
  "jmall.goods.saveorupdatepackagegoods": { url: "/jmall/goods/saveorupdatepackagegoods", type: "POST" },
  "jmall.goods.checkgoodsispackagegoods": { url: "/jmall/goods/checkgoodsispackagegoods", type: "POST" },
  "jmall.goods.blockupgoods": { url: "/jmall/goods/blockupgoods", type: "POST" },
  "jmall.goods.blockupgoodsspec": { url: "/jmall/goods/blockupgoodsspec", type: "POST" },
  "jmall.goods.deletegoods": { url: "/jmall/goods/deletegoods", type: "POST" },
  "jmall.goods.updatecategoryrichtext": { url: "/jmall/goods/updatecategoryrichtext", type: "POST" },
  "jmall.goods.getgoodslabellist": { url: "/jmall/goods/getgoodslabellist", type: "POST" },
  "jmall.goods.getgoodslabellistcount": { url: "/jmall/goods/getgoodslabellistcount", type: "POST" },
  "jmall.goods.setgoodslabelinfo": { url: "/jmall/goods/setgoodslabelinfo", type: "POST" },
  "jmall.goods.batchupdategoodslabelinfo": { url: "/jmall/goods/batchupdategoodslabelinfo", type: "POST" },
  "jmall.goods.deletegoodsllbel": { url: "/jmall/goods/deletegoodsllbel", type: "POST" },
  "jmall.goods.setlabelgoods": { url: "/jmall/goods/setlabelgoods", type: "POST" },
  "jmall.goods.getlablegoodsidlist": { url: "/jmall/goods/getlablegoodsidlist", type: "POST" },
  "jmall.goods.getgoodsgrouplistcount": { url: "/jmall/goods/getgoodsgrouplistcount", type: "POST" },
  "jmall.goods.setgoodsgroupinfo": { url: "/jmall/goods/setgoodsgroupinfo", type: "POST" },
  "jmall.goods.deletegoodsgroup": { url: "/jmall/goods/deletegoodsgroup", type: "POST" },
  "jmall.goods.getgroupgoodsidlsit": { url: "/jmall/goods/getgroupgoodsidlsit", type: "POST" },
  "jmall.goods.setgroupgoods": { url: "/jmall/goods/setgroupgoods", type: "POST" },
  "jmall.goods.getgoodsbrandlist": { url: "/jmall/goods/getgoodsbrandlist", type: "POST" },
  "jmall.goods.getgoodsbrandlistcount": { url: "/jmall/goods/getgoodsbrandlistcount", type: "POST" },
  "jmall.goods.syncjackyunbrandinfo": { url: "/jmall/goods/syncjackyunbrandinfo", type: "POST" },
  "jmall.goods.getmanagegoodscategorylist": { url: "/jmall/goods/getmanagegoodscategorylist", type: "POST" },
  "jmall.goods.updategoodsbasicinfo": { url: "/jmall/goods/updategoodsbasicinfo", type: "POST" },
  "jmall.goods.getmanagergoodsdetail": { url: "/jmall/goods/getmanagergoodsdetail", type: "POST" },
  "jmall.goods.getrecommendgoodslist": { url: "/jmall/goods/getrecommendgoodslist", type: "POST" },
  "jmall.goods.addgoodscategory": { url: "/jmall/goods/addgoodscategory", type: "POST" },
  "jmall.goods.deletegoodscategory": { url: "/jmall/goods/deletegoodscategory", type: "POST" },
  "jmall.goods.updategoodscategory": { url: "/jmall/goods/updategoodscategory", type: "POST" },
  "jmall.goods.updategoodscategorysort": { url: "/jmall/goods/updategoodscategorysort", type: "POST" },
  "jmall.goods.updategoodscategoryandgoodsinfo": { url: "/jmall/goods/updategoodscategoryandgoodsinfo", type: "POST" },
  "jmall.goods.getchannelgoodslist": { url: "/jmall/goods/getchannelgoodslist", type: "POST" },
  "jmall.goods.setchannelgoodsforchannel": { url: "/jmall/goods/setchannelgoodsforchannel", type: "POST" },
  "jmall.goods.getgoodsspecandunitlist": { url: "/jmall/goods/getgoodsspecandunitlist", type: "POST" },
  "jmall.goods.getgoodslistofsepclevel": { url: "/jmall/goods/getgoodslistofsepclevel", type: "POST" },
  "jmall.goods.updategoodsinfoformanage": { url: "/jmall/goods/updategoodsinfoformanage", type: "POST" },
  "jmall.goods.updateextrastock": { url: "/jmall/goods/updateextrastock", type: "POST" },
  "jmall.goods.getchannelgoodscount": { url: "/jmall/goods/getchannelgoodscount", type: "POST" }
};

/**
 * 营销活动相关接口
 */
const activityMethodList = {
  "jmall.activity.gettimedsaledetail": {
    url: "/jmall/activity/gettimedsaledetail",
    type: "POST"
  },
  "jmall.activity.listtimedsale": {
    url: "/jmall/activity/listtimedsale",
    type: "POST"
  },
  "jmall.activity.gettimedgoodsdetail": {
    url: "/jmall/activity/gettimedgoodsdetail",
    type: "POST"
  },
  // PC 端使用：带 .v1 后缀
  "jmall.activity.gettimedgoodsdetail.v1": {
    url: "/jmall/activity/gettimedgoodsdetail/v1",
    type: "POST"
  },
  // PC 端旧接口：jkymini 网关不支持，请改用 jmall.activity.timedsalelist（v1）
  "jmall.activity.gettimedsalelistbysaleid": {
    url: "/jmall/activity/gettimedsalelistbysaleid",
    type: "POST"
  },
  "jmall.activity.gettimedsalelistbyspec": {
    url: "/jmall/activity/gettimedsalelistbyspec",
    type: "POST"
  },
  "jmall.activity.getmarketinginfolistbyspec": {
    url: "/jmall/activity/getmarketinginfolistbyspec",
    type: "POST"
  },
  // PC 端使用：拼写错误（activiy），老后端已经接受这个拼写
  "jmall.activiy.updatepresalegoodscount": {
    url: "/jmall/activity/updatepresalegoodscount",
    type: "POST"
  },
  "jmall.activity.updatepresalegoodscount": {
    url: "/jmall/activity/updatepresalegoodscount",
    type: "POST"
  },
  // PC 端使用：拼写错误
  "jmall.activiy.listspecpresellInfobygoodsid": {
    url: "/jmall/activity/getpresalebygoodsid",
    type: "POST"
  },
  "jmall.activity.listspecpresellInfobygoodsid": {
    url: "/jmall/activity/getpresalebygoodsid",
    type: "POST"
  },
  "jmall.activity.getpresalegoodscount": {
    url: "/jmall/activity/getpresalegoodscount",
    type: "POST"
  },
  "jmall.activity.getpresalelist": {
    url: "/jmall/activity/getpresalelist",
    type: "POST"
  },
  "jmall.activity.timedsalelist": {
    url: "/jmall/activity/listtimedsale/v1",
    type: "POST"
  },
  "jmall.activity.gettimedsalegoodslist": {
    url: "/jmall/activity/gettimedsaledetail/v1",
    type: "POST"
  },
  "jmall.activity.getpromotionlist": {
    url: "/jmall/activity/sharesale/list",
    type: "POST"
  },
  "jmall.activity.getpromotiondetail": {
    url: "/jmall/activity/sharedetail/query",
    type: "POST"
  },
  "jmall.activity.getpromotionitemlist": {
    url: "/jmall/activity/sharesale/item/list",
    type: "POST"
  },
  "jmall.activity.setsharecontent": {
    url: "/jmall/activity/sharelink/generate",
    type: "POST"
  },
  "jmall.activity.getsharecontent": {
    url: "/jmall/activity/sharelink/query",
    type: "POST"
  },
  "jmall.activity.getconditionpromotionitemlist": {
    url: "/jmall/activity/sharesale/condition/itemlist",
    type: "POST"
  },
  "jmall.activity.savepromotionreceiverecord": {
    url: "/jmall/activity/sharerecord/save",
    type: "POST"
  },
  "jmall.activity.existsavailablecouponpromotion": {
    url: "/jmall/activity/sharesale/check/isexist",
    type: "POST"
  },
  "jmall.activity.getteambuydetail": {
    url: "/jmall/activity/getteambuydetail",
    type: "POST"
  },
  "jmall.activity.getteambuylist": {
    url: "/jmall/activity/getteambuylist",
    type: "POST"
  },
  "jmall.activity.getteambuygoodsdetail": {
    url: "/jmall/activity/getteambuygoodsdetail",
    type: "POST"
  },
  "jmall.activity.getteambuygroupdetail": {
    url: "/jmall/activity/getteambuygroupdetail",
    type: "POST"
  }
};

/**
 * 卡券相关接口
 */
const couponMethodList = {
  "jmall.coupon.getstopcouponlist": {
    url: "/jmall/coupon/getstopcouponlist",
    type: "POST"
  },
  "jmall.coupon.checkcouponcanuse": {
    url: "/jmall/coupon/checkcouponcanuse",
    type: "POST"
  },
  "jmall.coupon.addusecoupondetaildata": {
    url: "/jmall/trade/batch/save/coupon/use/detail",
    type: "POST"
  },
  "jmall.coupon.addusecoupondatabatch": {
    url: "/jmall/coupon/batch/addusecoupondata",
    type: "POST"
  },
  "jmall.coupon.canfreepostage": {
    url: "/jmall/coupon/canfreepostage",
    type: "POST"
  },
  "jmall.coupon.getjackyuncouponextendlist": {
    url: "/jmall/coupon/manage/getjackyuncouponextendlist",
    type: "POST"
  },
  "jmall.coupon.addusecoupondata": {
    url: "/jmall/coupon/addusecoupondata",
    type: "POST"
  },
  "jmall.coupon.getusercouponinfo": {
    url: "/jmall/coupon/getusercouponinfo",
    type: "POST"
  },
  // PC 端使用
  "jmall.coupon.checkcanrefund": {
    url: "/jmall/coupon/check/canrefund",
    type: "POST"
  },
  "jmall.coupon.listticketdetail": {
    url: "/jmall/coupon/list/ticket/simpledetail",
    type: "POST"
  },
  "jmall.coupon.pickupgoodsconsume": {
    url: "/jmall/coupon/pickupgoods/consumption",
    type: "POST"
  },
  "jmall.coupon.batch.pickupgoodsconsume": {
    url: "/jmall/coupon/batch/pickupgoods/consumption",
    type: "POST"
  },
  "jmall.coupon.getcouponusedetail": {
    url: "/jmall/trade/batch/query/coupon/use/detail",
    type: "POST"
  },
  "jmall.coupon.getcouponinfobyticketid": {
    url: "/pos/open/card/ticket/instance/receiveList",
    type: "GET"
  },
  "jmall.coupon.getcouponextendinfolist": {
    url: "/jmall/coupon/batch/query/ext",
    type: "POST"
  },
  "jmall.trade.sku.price.byfield": {
    url: "/jmall/trade/sku/price/byfield",
    type: "POST"
  },
  "jmall.coupon.checkcouponpwd": {
    url: "/jmall/coupon/check/ticket/pwd",
    type: "POST"
  },
  "jmall.coupon.getcoupondetail": {
    url: "/jmall/coupon/query/ticket/detail",
    type: "POST"
  },
  "jmall.coupon.getuserecord": {
    url: "/jmall/coupon/userecord/get",
    type: "POST"
  },
  "jmall.coupon.adduserecord": {
    url: "/jmall/coupon/userecord/add",
    type: "POST"
  },

  // 老的 coupon.* 走 admin
  "coupon.coupondetail": { url: "/coupon/coupondetail", type: "POST" },
  "coupon.couponmatch": { url: "/coupon/couponmatch", type: "POST" },
  "coupon.mycouponlist": { url: "/coupon/mycouponlist", type: "POST" },
  "coupon.receivelist": { url: "/coupon/receivelist", type: "POST" },
  "coupon.receivelistcount": { url: "/coupon/receivelistcount", type: "POST" },
  "coupon.couponbinding": { url: "/coupon/couponbinding", type: "POST" },
  "coupon.getcouponinstance": { url: "/coupon/getcouponinstance", type: "POST" },
  "coupon.couponuse": { url: "/coupon/couponuse", type: "POST" },
  "coupon.exchangerecord": { url: "/coupon/exchangerecord", type: "POST" },
  "coupon.recovery": { url: "/coupon/recovery", type: "POST" },
  "coupon.couponinstancelist": { url: "/coupon/couponinstancelist", type: "POST" },
  "coupon.pickcoupondetail": { url: "/coupon/pickcoupondetail", type: "POST" },
  "coupon.gradeequitycarduse": { url: "/coupon/gradeequitycarduse", type: "POST" }
};

/**
 * 地址相关接口
 */
const addressMethodList = {
  // 标准命名（小程序）
  "jmall.address.getaddresslist": {
    url: "/jmall/address/getcustomeraddresslist",
    type: "POST",
    bAuth: true
  },
  "jmall.address.setaddress": {
    url: "/jmall/address/setcustomeraddress",
    type: "POST",
    bAuth: true
  },
  "jmall.address.setdefaultaddress": {
    url: "/jmall/address/setdefaultcustomeraddress",
    type: "POST"
  },
  "jmall.address.deleteaddress": {
    url: "/jmall/address/deletecustomeraddress",
    type: "POST"
  },
  "jmall.address.analysis": {
    url: "/jmall/address/analysis",
    type: "POST"
  },
  "jmall.address.getaddressidbydetail": {
    url: "/jmall/address/getaddressidbydetail",
    type: "POST"
  },
  "jmall.address.tencentlbsgeocoder": {
    url: "/jmall/address/tencentlbsgeocoder",
    type: "POST"
  },
  "jmall.address.amaplbsgeocoder": {
    url: "/jmall/address/amaplbsgeocoder",
    type: "POST"
  },
  "jmall.address.getdeliveryserviceinfo": {
    url: "/jmall/postage/cfg/deliveryfee/list",
    type: "POST"
  },
  // PC 端使用：与小程序名字不同
  "jmall.address.getaddresslistcount": {
    url: "/jmall/address/getcustomeraddresslist/count",
    type: "POST"
  },
  "jmall.address.setaddressinfo": {
    url: "/jmall/address/setcustomeraddress",
    type: "POST"
  }
};

/**
 * 充值相关接口
 */
const rechargeMethodList = {
  "jmall.recharge.rechargecreate": {
    url: "/jmall/recharge/rechargecreate",
    type: "POST",
    bAuth: true
  },
  "jmall.recharge.getrechargeamount": {
    url: "/jmall/recharge/getrechargeamount",
    type: "POST"
  },
  "jmall.recharge.rechargepayconfirm": {
    url: "/jmall/recharge/updaterechargepayinfo",
    type: "POST"
  },
  // PC 端使用
  "jmall.recharge.getrechargedetail": {
    url: "/jmall/recharge/getrechargedetail",
    type: "POST"
  }
};

/**
 * 退款相关接口
 */
const refundMethodList = {
  "jmall.refund.refundlist": {
    url: "/jmall/refund/list",
    type: "POST",
    bAuth: true
  }
};

/**
 * 装修相关接口
 */
const styleMethodList = {
  "jmall.style.listcustomstyle": {
    url: "/jmall/style/listcustomstyle",
    type: "POST"
  },
  "jmall.style.getcustomercenterstylelist": {
    url: "/jmall/style/getcustomercenterstylelist",
    type: "POST"
  },
  // PC 端使用
  "jmall.style.getdesignlist": {
    url: "/jmall/style/design/batch/get",
    type: "POST"
  },
  // PC 端专属：取 PC 商城装修
  "jmall.style.getpcmallstyle": {
    url: "/jmall/style/getpcmallstyle",
    type: "POST"
  },
  "jmall.style.getsimpledesignlist": {
    url: "/jmall/style/design/batch/get/simple",
    type: "POST"
  },
  "jmall.style.getselfdesignchannelidlist": {
    url: "/jmall/style/getselfdesignchannelidlist",
    type: "POST"
  },
  "jmall.style.asyncchanneldesign": {
    url: "/jmall/style/asyncchanneldesign",
    type: "POST"
  }
};

/**
 * 组合装相关接口
 */
const comboMethodList = {
  "jmall.combo.listcombo": {
    url: "/jmall/combo/listcombo",
    type: "POST"
  },
  "jmall.combo.getcombodetail": {
    url: "/jmall/combo/getcombodetail",
    type: "POST"
  },
  "jmall.combo.getcombospecstock": {
    url: "/jmall/combo/getcombospecstock",
    type: "POST"
  }
};

/**
 * 分销相关接口
 */
const distributionMethodList = {
  "jmall.distribution.getapplyinfo": {
    url: "/jmall/distribution/getapplyinfo",
    type: "POST",
    bAuth: true
  },
  "jmall.distribution.adddistributorapply": {
    url: "/jmall/distribution/adddistributorapply",
    type: "POST",
    bAuth: true
  },
  "jmall.distribution.updateapplystatus": {
    url: "/jmall/distribution/updateapplystatus",
    type: "POST",
    bAuth: true
  },
  "jmall.distribution.getachievementinfo": {
    url: "/jmall/distribution/getachievementinfo",
    type: "POST",
    bAuth: true
  },
  "jmall.distribution.addsettlementapply": {
    url: "/jmall/distribution/addsettlementapply",
    type: "POST",
    bAuth: true
  },
  "jmall.distribution.commisionrankinglist": {
    url: "/jmall/distribution/commisionranking",
    type: "POST"
  },
  "jmall.distribution.getfullcommissionlist": {
    url: "/jmall/distribution/getfullcommissionlist",
    type: "POST"
  },
  "jmall.distribution.geteffectivecommissionlist": {
    url: "/jmall/distribution/geteffectivecommissionlist",
    type: "POST"
  },
  "jmall.distribution.getapplycommissionlist": {
    url: "/jmall/distribution/getapplycommissionlist",
    type: "POST"
  },
  "jmall.distribution.getprojectedcommissionlist": {
    url: "/jmall/distribution/getprojectedcommissionlist",
    type: "POST"
  },
  "jmall.distribution.getlowercustomerlist": {
    url: "/jmall/distribution/getlowercustomerlist",
    type: "POST"
  },
  "jmall.distribution.getpromotetradelist": {
    url: "/jmall/distribution/getpromotetradelist",
    type: "POST"
  },
  "jmall.distribution.getdistributionposterlist": {
    url: "/jmall/distribution/getdistributionposterlist",
    type: "POST"
  },
  "jmall.distribution.getdistributiongoodslist": {
    url: "/jmall/distribution/getdistributiongoodslist",
    type: "POST"
  },
  "jmall.distribution.updateupperid": {
    url: "/jmall/distribution/updateupperid",
    type: "POST",
    bAuth: true
  },
  "jmall.distribution.setdisshopgoodsinfo": {
    url: "/jmall/distribution/innershop/goods/save",
    type: "POST",
    bAuth: true
  },
  "jmall.distribution.setdisshopmaininfo": {
    url: "/jmall/distribution/innershop/shop/save",
    type: "POST",
    bAuth: true
  },
  "jmall.distribution.getdisshopmaininfo": {
    url: "/jmall/distribution/innershop/shop/query",
    type: "POST"
  },
  "jmall.distribution.setdisshopgoodspriceinfo": {
    url: "/jmall/distribution/innershop/spec/save",
    type: "POST"
  },
  "jmall.distribution.getdisshopspeccount": {
    url: "/jmall/distribution/innershop/spec/count",
    type: "POST"
  },
  "jmall.distribution.checkisdistributor": {
    url: "/jmall/distribution/is/distributor",
    type: "POST"
  }
};

/**
 * 积分互动相关接口
 */
const interactionMethodList = {
  "jmall.interaction.getsignsetting": {
    url: "/jmall/customer/sign/getsignsetting",
    type: "POST"
  },
  "jmall.interaction.getpersonalsigninfo": {
    url: "/jmall/customer/sign/getsigninfo",
    type: "POST"
  },
  "jmall.interaction.customersignin": {
    url: "/jmall/customer/sign/customersignin",
    type: "POST"
  },
  "jmall.interaction.getcustomertasklist": {
    url: "/jmall/customer/sign/gettaskinfolist",
    type: "POST"
  },
  "jmall.interaction.dotask": {
    url: "/jmall/customer/sign/dotask",
    type: "POST"
  },
  "jmall.interaction.getlotterysetting": {
    url: "/jmall/customer/interact/getactivitydetail",
    type: "POST"
  },
  "jmall.interaction.lotteryreordlist": {
    url: "/jmall/customer/interact/drawlotteryreordlist",
    type: "POST"
  },
  "jmall.interaction.getlotterylist": {
    url: "/jmall/customer/interact/getactivitylist",
    type: "POST"
  },
  "jmall.interaction.getlotterydetail": {
    url: "/jmall/customer/interact/getactivitydetail",
    type: "POST"
  },
  "jmall.interaction.drawlottery": {
    url: "/jmall/customer/interact/drawlottery",
    type: "POST"
  },
  "jmall.interaction.receivelotteryprize": {
    url: "/jmall/customer/interact/receiveprize",
    type: "POST"
  },
  "jmall.interaction.receivelotteryprizev1": {
    url: "/jmall/customer/interact/receiveprize/v1",
    type: "POST"
  },
  "jmall.interaction.shareaddlotterytimes": {
    url: "/jmall/customer/interact/addsharerecord",
    type: "POST"
  },
  "jmall.praise.addpraise": {
    url: "/jmall/praise/addpraise",
    type: "POST"
  },
  "jmall.praise.getappliedtradeno": {
    url: "/jmall/praise/tradenolist",
    type: "POST"
  },
  "jmall.goods.geterpgoodsprice": {
    url: "/jmall/goods/geterpgoodsprice",
    type: "POST"
  }
};

/**
 * 支付相关接口
 */
const payMethodList = {
  "jmall.pay.unifiedorder": {
    url: "/jmall/pay/unifiedorder",
    type: "POST"
  },
  "jmall.pay.gettongliancashierparamsforwechat": {
    url: "/jmall/pay/getunionpayinfo",
    type: "POST"
  },
  "jmall.pay.getplatformpayinfo": {
    url: "/jmall/pay/query",
    type: "POST"
  },
  "jmall.pay.getbanklist": {
    url: "/jmall/pay/get/largepay/bank",
    type: "POST"
  }
};

/**
 * 其他接口
 */
const otherMethodList = {
  "jmall.notice.listnotices": {
    url: "/jmall/notice/listnotices",
    type: "POST"
  },
  "jmall.restricted.checkrestrictedpurchase": {
    url: "/jmall/restricted/checkrestrictedpurchase",
    type: "POST"
  },
  "jmall.hotsearch.gethotsearchcfglist": {
    url: "/jmall/hotsearch/gethotsearchcfglist",
    type: "POST"
  },
  "jmall.data.addhitscount": {
    url: "/jmall/other/addhitscount",
    type: "POST"
  },
  // PC 端使用
  "jmall.jackyunlog.saveloginfo": {
    url: "/jmall/jackyunlog/saveloginfo",
    type: "POST"
  },
  "jmall.event.add": {
    url: "/jmall/event/log/add",
    type: "POST"
  },
  "jmall.event.track.enabled": {
    url: "/jmall/event/log/track/enabled",
    type: "POST"
  },
  "jmall.other.decryptparamsforgieseppe": {
    url: "/jmall/other/aes/decrypt",
    type: "POST"
  },
  "jmall.other.encryptparamsforgieseppe": {
    url: "/jmall/other/aes/encrypt",
    type: "POST"
  },
  "jmall.other.getaccesstoken": {
    url: "/jmall/jkylogin/jkylogin/get/accessToken",
    type: "POST"
  }
};

/**
 * 系统接口（验证库相关）
 */
const authMethodList = {
  "jmall.auth.addlog": {
    url: "/jmall/sys/addauthsyslog",
    type: "POST"
  },
  "jmall.auth.getsubmallinfolist": {
    url: "/jmall/sys/getsubaccountinfo",
    type: "POST"
  },
  "jmall.auth.getjmallproductorderinfo": {
    url: "/jmall/sys/getjmallproductorderinfo",
    type: "POST"
  },
  "jmall.auth.getsysconfig": {
    url: "/jmall/sys/getauthcfgsetting",
    type: "POST"
  }
};

/**
 * 架构登录相关接口（这些不走 jkymini 网关，走的是 jackyunUrl 下的 /auth/toc/login）
 */
const loginMethodList = {
  "jmall.login.componentlogin": {
    url: "/auth/toc/login/componentLogin",
    type: "GET"
  },
  "jmall.login.componentauth": {
    url: "/auth/toc/login/componentAuth",
    type: "GET"
  },
  // 游客态假登录和账密登录都走这个
  "jmall.login.accountlogin": {
    url: "/auth/toc/login/accountLogin",
    type: "GET"
  },
  "jmall.login.tokenrefresh": {
    url: "/auth/refresh",
    type: "GET"
  },
  "jmall.login.fastLogin": {
    url: "/auth/toc/login/fastLogin",
    type: "GET"
  }
};

/**
 * 微信开放平台接口
 */
const wechatPlatformMethodList = {
  "jmall.wxplat.getopenid": {
    url: "/jmall-single/wx/plat/getwxopenid",
    type: "POST"
  },
  "jmall.wxplat.getunlimitedqrcode": {
    url: "/jmall-single/wx/plat/getunlimitedqrcode",
    type: "POST"
  },
  "jmall.wxplat.getdecryptphone": {
    url: "/jmall/weixin/plat/wxphonedecrypt",
    type: "POST"
  },
  // PC 端使用：注意拼写 shotlink 不是 shortlink
  "jmall.wxplat.getshotlink": {
    url: "/jmall-single/wx/plat/getshotlink",
    type: "POST"
  }
};

/**
 * 渠道、仓库、物流相关（PC 后台 switch 中处理，不走 jkymini）
 */
const channelMethodList = {
  // salechannel.* 走 admin
  "salechannel.list": { url: "/salechannel/list", type: "POST" },
  "salechannel.listneed": { url: "/salechannel/listneed", type: "POST" },
  "salechannel.listbyid": { url: "/salechannel/listbyid", type: "POST" },
  "salechannel.detail": { url: "/salechannel/detail", type: "POST" },
  "salechannel.accountlist": { url: "/salechannel/accountlist", type: "POST" },
  "salechannel.getlist": { url: "/salechannel/getlist", type: "POST" },
  "logistic.list": { url: "/logistic/list", type: "POST" },
  "logistic.get": { url: "/logistic/get", type: "POST" },
  "salechannel.defaultaccount": { url: "/salechannel/defaultaccount", type: "POST" },
  "account.alllist": { url: "/account/alllist", type: "POST" },
  "account.detail": { url: "/account/detail", type: "POST" },
  "account.authinfo": { url: "/account/authinfo", type: "POST" },
  "account.getwechatcfg": { url: "/account/getwechatcfg", type: "POST" },
  "account.getwechatcfgbyid": { url: "/account/getwechatcfgbyid", type: "POST" },
  "account.getaccountinfo": { url: "/account/getaccountinfo", type: "POST" },
  "whole.shoplist": { url: "/whole/shoplist", type: "POST" },
  "whole.warehouselist": { url: "/whole/warehouselist", type: "POST" }
};

/**
 * PC 后台自有客户档案相关（不走 jkymini）
 */
const customerAdminMethodList = {
  "sms.getsigaturelist": { url: "/sms/getsigaturelist", type: "POST" },
  "customer.getcustomer": { url: "/customer/getcustomer", type: "POST" },
  "customer.getcustomernew": { url: "/customer/getcustomernew", type: "POST" },
  "customer.getcustomerbyid": { url: "/customer/getcustomerbyid", type: "POST" },
  "customer.getdiscountgoodsinfo": { url: "/customer/getdiscountgoodsinfo", type: "POST" },
  "customer.getcustomerdetail": { url: "/customer/getcustomerdetail", type: "POST" },
  "customer.getcustomerbyphone": { url: "/customer/getcustomerbyphone", type: "POST" },
  "customer.searchforstore": { url: "/customer/searchforstore", type: "POST" },
  "customer.getcustomerlist": { url: "/customer/getcustomerlist", type: "POST" },
  "customer.getcustomerbyids": { url: "/customer/getcustomerbyids", type: "POST" },
  "customer.addcustomer": { url: "/customer/addcustomer", type: "POST" },
  "customer.editcustomer": { url: "/customer/editcustomer", type: "POST" },
  "customer.editcustomercomplete": { url: "/customer/editcustomercomplete", type: "POST" },
  "customer.gradelist": { url: "/customer/gradelist", type: "POST" },
  "customer.count": { url: "/customer/count", type: "POST" },
  "customer.updatescore": { url: "/customer/updatescore", type: "POST" },
  "customer.balance": { url: "/customer/balance", type: "POST" },
  "customer.balancebycom": { url: "/customer/balancebycom", type: "POST" },
  "customer.ewalletbalance": { url: "/customer/ewalletbalance", type: "POST" },
  "customer.getgradediscount": { url: "/customer/getgradediscount", type: "POST" },
  "customer.recharge": { url: "/customer/recharge", type: "POST" },
  "customer.tradetotal": { url: "/customer/tradetotal", type: "POST" },
  "customer.integralloglist": { url: "/customer/integralloglist", type: "POST" },
  "customer.rechargeloglist": { url: "/customer/rechargeloglist", type: "POST" },
  "customer.consumeloglist": { url: "/customer/consumeloglist", type: "POST" },
  "customer.arapdetail": { url: "/customer/arapdetail", type: "POST" },
  "customer.muticonditionquery": { url: "/customer/muticonditionquery", type: "POST" },
  "customer.getvipleveldetail": { url: "/customer/getvipleveldetail", type: "POST" },
  "customer.listcustomersource": { url: "/customer/listcustomersource", type: "POST" },
  "customer.gettaglist": { url: "/customer/gettaglist", type: "POST" },
  "customer.getcrmcustomfield": { url: "/customer/getcrmcustomfield", type: "POST" },
  "customerWxAccount.getEnabledWxAccountList": {
    url: "/customerWxAccount/getEnabledWxAccountList",
    type: "POST"
  }
};

/**
 * 周期购、运营策略（PC 后台 switch 处理）
 */
const cyclePolyMethodList = {
  "cycle.maintradelist": { url: "/cycle/maintradelist", type: "POST" },
  "cycle.subtradelist": { url: "/cycle/subtradelist", type: "POST" },
  "cycle.goodslist": { url: "/cycle/goodslist", type: "POST" },
  "cycle.updatesenddate": { url: "/cycle/updatesenddate", type: "POST" },
  "cycle.updatereceiverinfo": { url: "/cycle/updatereceiverinfo", type: "POST" },
  "poly.polylist": { url: "/poly/polylist", type: "POST" },
  "poly.polylistcount": { url: "/poly/polylistcount", type: "POST" },
  "poly.polyremind": { url: "/poly/polyremind", type: "POST" },
  "poly.polymatch": { url: "/poly/polymatch", type: "POST" }
};

/**
 * 工具类接口（不走 jkymini，PC 后台管理员调用）
 */
const utilMethodList = {
  // 系统时间戳
  "sys.getsystimestamp": { url: "/sys/getsystimestamp", type: "POST" },
  // 系统设置（PC 用）
  "sys.getsetting": { url: "/sys/getsetting", type: "POST" },
  // PC 端专属：将商品加到购物车
  "pc.trade.addtradegoodstocart": { url: "/pc/trade/addtradegoodstocart", type: "POST" },
  // 老的 app.* 接口
  "app.trade.gettrademanager": { url: "/app/trade/gettrademanager", type: "POST" }
};

/**
 * 合并所有 method
 */
const methodList = {
  ...baseMethodList,
  ...customerMethodList,
  ...customerAdminMethodList,
  ...tradeMethodList,
  ...goodsMethodList,
  ...activityMethodList,
  ...couponMethodList,
  ...addressMethodList,
  ...rechargeMethodList,
  ...refundMethodList,
  ...styleMethodList,
  ...comboMethodList,
  ...distributionMethodList,
  ...interactionMethodList,
  ...payMethodList,
  ...otherMethodList,
  ...authMethodList,
  ...loginMethodList,
  ...wechatPlatformMethodList,
  ...channelMethodList,
  ...cyclePolyMethodList,
  ...utilMethodList
};

/**
 * 是否走 jkymini 网关（基于 method 前缀判断）
 *  - jmall.* method 走 jkymini 网关（架构 C 端标准接口）
 *  - 其他前缀走 admin 后台（不走 jkymini）
 *
 * 注意：这种区分后续可按会员灰度，可在 request-utils.js 中加 useJkymini 全局开关
 */
function isJkyminiMethod(methodName) {
  if (!methodName) return false;
  return methodName.startsWith("jmall.") && !methodName.startsWith("jmall.login.");
}

/**
 * 是否走 C 端登录专用接口（不走 jkymini 网关，走 jackyunUrl/auth/toc/login）
 */
function isToCLoginMethod(methodName) {
  if (!methodName) return false;
  return methodName.startsWith("jmall.login.");
}

export {
  methodList,
  isJkyminiMethod,
  isToCLoginMethod
};
